/**
 * Server discovery for self-hosted DTS Mail: a domain publishes TXT `_dtsmail.<domain>` =
 * `v=dtsmail1 api=https://<app>.azurewebsites.net/api`, so typing an email address is enough to find its server.
 * Looked up over DNS-over-HTTPS (works in browsers, Electron and tests alike).
 */
const DOH_URL = 'https://cloudflare-dns.com/dns-query';
const TIMEOUT_MS = 4000;

/** The API URL in a `v=dtsmail1 api=<url>` record, or null when it isn't one (only https URLs are accepted). */
export function parseDiscoveryRecord(txt: string): string | null {
  // DNS JSON returns TXT data quoted, and long records split into several quoted strings.
  const value = txt.trim().replace(/^"|"$/g, '').replace(/"\s*"/g, '').trim();
  const parts = value.split(/\s+/);
  if (parts[0]?.toLowerCase() !== 'v=dtsmail1') return null;
  const api = parts.find((p) => p.toLowerCase().startsWith('api='))?.slice(4);
  if (!api) return null;
  try {
    const url = new URL(api);
    if (url.protocol !== 'https:' || url.username || url.password) return null;
    return url.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
}

/** The DTS Mail server for an email address's domain, or null (no record, lookup failed, or timed out). Never throws. */
export async function discoverServer(email: string, fetchImpl: typeof fetch = fetch): Promise<string | null> {
  const domain = email.trim().toLowerCase().split('@')[1]?.replace(/\.+$/, '');
  if (!domain || !/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) return null;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), TIMEOUT_MS);
  try {
    const res = await fetchImpl(`${DOH_URL}?name=${encodeURIComponent(`_dtsmail.${domain}`)}&type=TXT`, {
      headers: { accept: 'application/dns-json' },
      signal: ac.signal,
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { Status?: number; Answer?: { type?: number; data?: string }[] };
    if (json.Status !== 0) return null;
    for (const a of json.Answer ?? []) {
      if (a.type !== 16 || typeof a.data !== 'string') continue; // 16 = TXT
      const api = parseDiscoveryRecord(a.data);
      if (api) return api;
    }
    return null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
